import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import makeStyles from '@material-ui/core/styles/makeStyles';

import objectsClient from '../../../clients/objects';

const useStyles = makeStyles((theme) => ({
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  searchMenuItem: {
    backgroundColor: theme.palette.background.paper,
    cursor: 'default',
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
    position: 'sticky',
    top: 0,
    zIndex: 2,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

const ObjectSelectField = ({
  value,
  onChange,
  error,
  errorText,
  onBlur,
  disabled,
  label,
  locale,
  multiple,
  searchPlaceholder,
}) => {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Do not pass pagination: the API is configured with unlimited default
        // (strapi-app/config/api.js). A page cap (e.g. 500) hid objects from search.
        const raw = await objectsClient.find({
          locale,
          sort: 'Title:asc',
        });
        const list = Array.isArray(raw) ? raw : raw?.data ?? [];
        if (!cancelled) {
          setOptions(
            list.map((o) => ({
              id: String(o.documentId ?? o.id),
              title: o.Title || String(o.documentId ?? o.id),
            }))
          );
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.title.toLowerCase().includes(q));
  }, [options, searchTerm]);

  const handleChange = (e) => {
    if (multiple) {
      const v = e.target.value;
      onChange(typeof v === 'string' ? v.split(',') : v);
    } else {
      onChange(e.target.value);
    }
  };

  const handleBlur = (e) => {
    if (onBlur) onBlur(e);
  };

  const handleMenuClose = () => {
    setSearchTerm('');
  };

  const selectedIds = multiple
    ? (Array.isArray(value) ? value.map(String) : [])
    : [];
  const selectedSet = multiple ? new Set(selectedIds) : null;

  const removeSelectedId = (idToRemove) => {
    onChange(selectedIds.filter((id) => id !== idToRemove));
  };

  const renderMultiValue = (selected) => {
    const ids = typeof selected === 'string' ? selected.split(',') : selected;
    if (!ids || ids.length === 0) return null;
    const list = Array.isArray(ids) ? ids.map(String) : [];
    return (
      <Box className={classes.chips}>
        {list.map((id) => {
          const title = options.find((o) => o.id === id)?.title || id;
          return (
            <Chip
              key={id}
              label={title}
              size="small"
              onMouseDown={(e) => e.stopPropagation()}
              onDelete={
                disabled
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      removeSelectedId(id);
                    }
              }
            />
          );
        })}
      </Box>
    );
  };

  const searchMenuItem = (
    <MenuItem
      key="__object-search__"
      className={classes.searchMenuItem}
      disableRipple
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.preventDefault()}
    >
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder={searchPlaceholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        autoComplete="off"
        disabled={disabled}
      />
    </MenuItem>
  );

  const menuListProps = {
    autoFocusItem: false,
    disableListWrap: true,
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={56}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (multiple) {
    return (
      <FormControl fullWidth variant="outlined" error={Boolean(error)} disabled={disabled}>
        <InputLabel id="prize-objects-select-label">{label}</InputLabel>
        <Select
          labelId="prize-objects-select-label"
          multiple
          value={selectedIds}
          onChange={handleChange}
          onBlur={handleBlur}
          onClose={handleMenuClose}
          label={label}
          disabled={disabled}
          renderValue={renderMultiValue}
          MenuProps={{
            autoFocus: false,
            disableAutoFocusItem: true,
            MenuListProps: menuListProps,
            PaperProps: {
              style: { maxHeight: 400 },
            },
            getContentAnchorEl: null,
          }}
        >
          {searchMenuItem}
          {filteredOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={selectedSet.has(o.id)}
                  tabIndex={-1}
                  disableRipple
                  color="primary"
                />
              </ListItemIcon>
              <ListItemText primary={o.title} />
            </MenuItem>
          ))}
        </Select>
        {error && errorText ? <FormHelperText>{errorText}</FormHelperText> : null}
      </FormControl>
    );
  }

  const singleValue = value || '';

  const renderSingleValue = (selected) => {
    if (selected == null || selected === '') return null;
    const item = options.find((o) => o.id === String(selected));
    if (!item) return selected;
    return (
      <Box className={classes.chips}>
        <Chip key={item.id} label={item.title} size="small" />
      </Box>
    );
  };

  return (
    <FormControl fullWidth variant="outlined" error={Boolean(error)} disabled={disabled}>
      <InputLabel id="prize-object-select-label">{label}</InputLabel>
      <Select
        labelId="prize-object-select-label"
        value={singleValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onClose={handleMenuClose}
        label={label}
        disabled={disabled}
        renderValue={renderSingleValue}
        MenuProps={{
          autoFocus: false,
          disableAutoFocusItem: true,
          MenuListProps: menuListProps,
          PaperProps: {
            style: { maxHeight: 400 },
          },
          getContentAnchorEl: null,
        }}
      >
        {searchMenuItem}
        <MenuItem value="">
          <em>Pasirinkite objektą</em>
        </MenuItem>
        {filteredOptions.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {o.title}
          </MenuItem>
        ))}
      </Select>
      {error && errorText ? <FormHelperText>{errorText}</FormHelperText> : null}
    </FormControl>
  );
};

ObjectSelectField.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  locale: PropTypes.string,
  multiple: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
};

ObjectSelectField.defaultProps = {
  value: '',
  error: false,
  errorText: '',
  onBlur: undefined,
  disabled: false,
  label: 'Objektas',
  locale: 'lt-LT',
  multiple: false,
  searchPlaceholder: 'Ieškoti pagal pavadinimą…',
};

export default ObjectSelectField;
